import { InfoCircleOutlined } from '@ant-design/icons'
import { DatePicker } from 'antd'
import moment from 'moment'
import { useMemo } from 'react'
import styled from 'styled-components'
import { FormItem } from 'sutra/components/Form'
import { Input } from 'sutra/components/Input/Input'
import { Tooltip } from 'sutra/components/Tooltip/Tooltip'

import { FormikType } from 'features/project-form/features/source/components/NewSource'
import { extractUrl } from 'features/project-form/features/source/utils'
import {
  sourceTypes,
  useSourceTypes,
} from 'features/project-form/utils/source-types'

type Props = {
  formik: FormikType
  sourceType: string
}
const dateFormat = 'MM/DD/YYYY'

export const Reamaze = ({ formik, sourceType }: Props): JSX.Element => {
  const { data: catalogSourceTypes } = useSourceTypes()
  const resolvedSourceTypes = catalogSourceTypes ?? sourceTypes
  const sourceUrlValue = useMemo(
    () =>
      extractUrl(
        formik.values.sourceUrl,
        resolvedSourceTypes[sourceType] ?? '',
      ),
    [formik.values.sourceUrl, sourceType, resolvedSourceTypes],
  )
  return (
    <>
      <FormItem
        label={
          <Tooltip content="Give your source a name to display in the charts. Make sure it's one you'll recognize.">
            <span>
              Source Title <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={formik.errors.sourceTitle ? 'error' : ''}
        help={formik.errors.sourceTitle}
        wrapperCol={{ span: 10 }}
      >
        <Input
          name="sourceTitle"
          onChange={formik.handleChange}
          value={formik.values.sourceTitle}
          $error={Boolean(formik.errors.sourceTitle)}
          placeholder="Insert title here"
        />
      </FormItem>
      <FormItem
        label={
          <Tooltip content="Select the earliest date from which data should be pulled.">
            <span>
              Date <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        wrapperCol={{ span: 16 }}
        validateStatus={formik.errors.sourceDate ? 'error' : ''}
        help={formik.errors.sourceDate}
      >
        <DatePicker
          defaultValue={
            formik.values.sourceDate && moment(formik.values.sourceDate)
          }
          onChange={(value) => {
            if (value) {
              formik.setFieldValue('sourceDate', value.toDate())
            }
          }}
          format={dateFormat}
        />
      </FormItem>
      <FormItem
        label={
          <Tooltip content="Provide the URL of the account.">
            <span>
              URL <InfoCircleOutlined />
            </span>
          </Tooltip>
        }
        validateStatus={formik.errors.sourceUrl ? 'error' : ''}
        help={formik.errors.sourceUrl}
        wrapperCol={{ span: 16 }}
      >
        <Flex>
          {resolvedSourceTypes[sourceType]}
          <Input
            name="sourceUrl"
            onChange={formik.handleChange}
            value={sourceUrlValue}
            $error={Boolean(formik.errors.sourceUrl)}
          />
        </Flex>
      </FormItem>
      <FormItem label="Brand" wrapperCol={{ span: 16 }}>
        <Input
          name="brand"
          onChange={formik.handleChange}
          value={formik.values.brand || ''}
          placeholder="Insert brand here"
        />
      </FormItem>
      <FormItem label="Email" wrapperCol={{ span: 16 }}>
        <Input
          name="email"
          onChange={formik.handleChange}
          value={formik.values.email || ''}
          placeholder="Insert email here"
        />
      </FormItem>
      <FormItem
        label="API Key"
        validateStatus={formik.errors.access_token ? 'error' : ''}
        help={formik.errors.access_token}
        wrapperCol={{ span: 16 }}
      >
        <Input
          name="access_token"
          onChange={formik.handleChange}
          value={formik.values.access_token || ''}
          $error={Boolean(formik.errors.access_token)}
          placeholder="Insert API Key here"
        />
      </FormItem>
    </>
  )
}

const Flex = styled.div`
  display: flex;
  align-items: center;
`
